import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { icon, label, number } from "../../../../config";
import { filterData } from "../../../../utils/kendo";
import { DrutasDropdown } from "../../../DrutasDropdown/DrutasDropdown";
import { getAssigneeData, handleGetProjectWorkflows, isUserPartOfProject } from "../../sidebar.helper";

/**
 * ApprovalAssigneeNew component - Merged DD3 + DD4
 * Parent component => ApprovalAssignment
 * @returns JSX
 * @author Sejal
 */
const ApprovalAssigneeNew = (props) => {
  const {
    approvalAssignType,
    approvalAssignId,
    approvalRelatedAssignedType,
    setApprovalRelatedAssignedType,
    approvalRelatedAssignedId,
    setApprovalRelatedAssignedId,
    relatedAssignedIdList,
    setRelatedAssignedIdList,
    disable,
  } = props;

  const [filterText, setFilterText] = useState("");
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { user } = useSelector((state) => state.auth);

  /**
   * Gets the disable condition for DD3+DD4 in approvals
   * @returns {Boolean}
   * @author Sejal
   */
  const getDisableCondition = () => {
    const userProjectList = defaultDetails?.allProjectsList?.filter((project) => {
      return project.RoleId || (project.IsPersonal && project.user === user.id);
    });
    return disable || !userProjectList?.find((p) => p.value === approvalAssignId?.ProjectId);
  };

  /**
   * Gets combined list of users and workflows for DD4
   * @returns {Array}
   * @author Sejal
   */
  const getDD4CombinedList = async () => {
    if (!approvalAssignId) return [];

    const isUserPart = isUserPartOfProject(defaultDetails, approvalAssignId);
    let combinedList = [];

    // Get assignee list (users)
    let assigneeList = getAssigneeData(approvalAssignId, defaultDetails);
    assigneeList = assigneeList?.map((assignee) => ({
      ...assignee,
      RelatedAssignmentType: number.TWO,
    })) || [];

    // Get workflow list
    const projectId = approvalAssignId?.AssignmentType === number.TWO
      ? approvalAssignId?.myProjectId
      : approvalAssignId?.value;

    let workflows = await handleGetProjectWorkflows(parseInt(projectId));
    workflows = workflows?.filter((workflow) => workflow.IsActive);
    workflows = workflows?.map(({ WorkflowId, WorkflowName, Icon, WorkflowPrivacy, IsDefault }) => ({
      value: WorkflowId,
      label: WorkflowName,
      privacy: WorkflowPrivacy,
      icon: Icon,
      isDefault: IsDefault,
      RelatedAssignmentType: number.FIVE,
    })) || [];

    // Combine based on user's project membership
    if (isUserPart) {
      combinedList = [...workflows, ...assigneeList];
    } else {
      combinedList = [...workflows];
    }

    return combinedList;
  };

  /**
   * Fetches and filters the assignment list
   * @param {Object} event
   * @author Sejal
   */
  const getAndFilterAssignmentList = async (event) => {
    setFilterText(event?.target?.value ? event?.target?.value : "");
    let list = await getDD4CombinedList();
    
    const customFilter = event
      ? {
          logic: "or",
          filters: [
            { field: "Email", operator: "contains", value: event.target.value },
            { field: "label", operator: "contains", value: event.target.value },
          ],
        }
      : null;
    
    list = event ? filterData(customFilter, list) : list;
    setRelatedAssignedIdList(list);
  };

  /**
   * Handles change in DD4
   * @param {Object} selectedItem
   * @author Cursor AI
   */
  const handleChange = async (selectedItem) => {
    if (!selectedItem) return;

    const relatedType = defaultDetails?.relatedAssignmentTypeList?.find(
      (type) => type.value === selectedItem?.RelatedAssignmentType
    );
    setApprovalRelatedAssignedType(relatedType);
    setApprovalRelatedAssignedId(selectedItem);
  };

  const clearFilterText = () => {
    getAndFilterAssignmentList();
  };

  /**
   * Custom dropdown item render
   * @returns JSX
   * @author Sejal
   */
  const itemRenderNew = (item, handleOnClick) => {
    return (
      <>
        {item?.RelatedAssignmentType === number.TWO ? (
          <div
            className="d-flex align-items-center drutas-dropdown-popup-element px-3 py-2 truncate-text cursor-pointer"
            onClick={() => handleOnClick(item)}
          >
            <div className="profile-picture-container mr-3">
              {item?.PhotoLinks?.small ? (
                <img src={item?.PhotoLinks?.small} className="profile-picture" />
              ) : (
                <span>{icon.USER_ICON}</span>
              )}
            </div>
            <div className="truncate-text">
              <div className="assignee-name truncate-text">{item?.label}</div>
              <div className="assignee-email truncate-text">{item?.Email}</div>
            </div>
          </div>
        ) : (
          <div
            className="d-flex align-items-center drutas-dropdown-popup-element px-3 py-2 truncate-text cursor-pointer"
            onClick={() => handleOnClick(item)}
          >
            <div className="profile-picture-container mr-3">
              {icon[item?.icon] ? icon[item?.icon] : ""}
            </div>
            <div className="truncate-text d-flex align-items-center">
              <div className="assignee-name truncate-text">{item?.label}</div>
            </div>
          </div>
        )}
      </>
    );
  };

  /**
   * Custom dropdown value render
   * @returns JSX
   * @author Sejal
   */
  const valueRenderNew = (item) => {
    return (
      <>
        {item?.RelatedAssignmentType === number.TWO ? (
          <div className="d-flex align-items-center justify-content-between drutas-dropdown-popup-element truncate-text">
            <div className="d-flex align-items-center">
              <div className="image-box mr-3">
                {item?.PhotoLinks?.small ? (
                  <img src={item?.PhotoLinks?.small} />
                ) : (
                  <span>{icon.USER_ICON}</span>
                )}
              </div>
              <div className="truncate-text">
                <div className="assignee-name truncate-text">{item?.label}</div>
              </div>
            </div>
            <span>{icon.ARROW_DROP_DOWN_ICON}</span>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-between drutas-dropdown-popup-element truncate-text">
            <div className="d-flex align-items-center truncate-text">
              <div className="image-box mr-3">
                <span className="assignee-icon">{icon[item?.icon] ? icon[item?.icon] : ""}</span>
              </div>
              <div className="truncate-text">
                <div className="assignee-name truncate-text">{item?.label}</div>
              </div>
            </div>
            <span>{icon.ARROW_DROP_DOWN_ICON}</span>
          </div>
        )}
      </>
    );
  };

  const filtersContent = () => {
    return (
      <div className="p-3 pb-2">
        <div className="assignto-search position-relative">
          <span className="assignto-search-icon">{icon.SEARCH}</span>
          <input
            autoFocus={true}
            className="assignto-search-field form-control w-100"
            placeholder="Search"
            value={filterText}
            onChange={getAndFilterAssignmentList}
          />
        </div>
      </div>
    );
  };

  // Only render if approvalRelatedAssignedType has a value
  if (!approvalRelatedAssignedType?.value) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="form-group col-lg-6 col-md-6 col-sm-6 d-flex align-items-center task-assignee-column">
        <div className="d-flex align-items-center assignee-field-wrapper w-100">
          <div className="assignee-field-inner d-flex">
            <div className="w-100 assignee-field-box">
              <label className="d-flex align-items-center" htmlFor="">
                {approvalRelatedAssignedId?.RelatedAssignmentType === number.FIVE ? label.WORKFLOWS : label.USER_WORKFLOWS}
              </label>
              <div>
                <DrutasDropdown
                  value={approvalRelatedAssignedId ? approvalRelatedAssignedId : null}
                  onChange={handleChange}
                  data={relatedAssignedIdList}
                  label={"label"}
                  key={"value"}
                  isfilter={true}
                  filterContent={filtersContent}
                  itemRender={itemRenderNew}
                  valueRender={valueRenderNew}
                //   disabled={getDisableCondition()}
                  popupClass="approval-dd-height"
                  clearFilterText={clearFilterText}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ApprovalAssigneeNew;

